'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

type Pill = { pos: string; icon: string; val: string; label: string };
type Metric = { val: string; label: string };

export type CaseStudyData = {
  brand: string;
  navLabel?: string;
  sector: string;
  platform: string;
  story: string;
  metrics: Metric[];
  chips: string[];
  accentColor: string;
  glowColor: string;
  chipBg: string;
  chipBorder: string;
  bgGrads: string[];
  phoneFeed: string[];
  phoneReels: number[];
  feedImage?: string;
  pills: Pill[];
  note: string;
};

const isImgUrl = (s: string) => s.startsWith('/') || s.startsWith('http');

// Preload toutes les images de tous les cas pour éviter le flash au changement
function usePreloadImages(cases: CaseStudyData[]) {
  useEffect(() => {
    const urls: string[] = [];
    for (const c of cases) {
      if (c.feedImage) urls.push(c.feedImage);
      for (const s of c.bgGrads) if (isImgUrl(s)) urls.push(s);
      for (const s of c.phoneFeed) if (isImgUrl(s)) urls.push(s);
    }
    for (const url of urls) {
      const img = new window.Image();
      img.src = url;
    }
  }, [cases]);
}

export default function CaseShowcase({ cases }: { cases: CaseStudyData[] }) {
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [displayIdx, setDisplayIdx] = useState(0);
  const dragStart = useRef<number | null>(null);
  const dragged = useRef(false);

  usePreloadImages(cases);

  const go = useCallback(
    (newIdx: number) => {
      if (newIdx === idx) return;
      setFading(true);
      setTimeout(() => {
        setDisplayIdx(newIdx);
        setFading(false);
      }, 240);
      setIdx(newIdx);
    },
    [idx],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    dragStart.current = e.clientX;
    dragged.current = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragStart.current !== null && Math.abs(e.clientX - dragStart.current) > 6)
      dragged.current = true;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (dragStart.current === null) return;
    const delta = e.clientX - dragStart.current;
    dragStart.current = null;
    if (!dragged.current) return;
    if (delta < -40) go(Math.min(idx + 1, cases.length - 1));
    else if (delta > 40) go(Math.max(idx - 1, 0));
  };

  const c = cases[displayIdx];
  const fade = { opacity: fading ? 0 : 1, transition: 'opacity 0.24s ease' };

  return (
    <div
      className="gr-sc"
      style={
        {
          '--case-accent': c.accentColor,
          '--case-glow': c.glowColor,
          '--case-chip-bg': c.chipBg,
          '--case-chip-border': c.chipBorder,
        } as React.CSSProperties
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* ── Stage visuel ── */}
      <div className="gr-sc-stage" style={{ ...fade, cursor: 'grab' }}>
        <div className="gr-sc-halo" />

        {/* Frames éparpillées */}
        <div className="gr-sc-frames">
          {c.bgGrads.slice(0, 6).map((src, i) => (
            <div key={i} className={`gr-sc-frame gr-sc-frame--${i}`}>
              <div
                className="gr-sc-frame-inner"
                style={
                  isImgUrl(src)
                    ? ({ background: `url(${src}) center/cover no-repeat` } as React.CSSProperties)
                    : ({ '--fr-grad': src } as React.CSSProperties)
                }
              />
            </div>
          ))}
        </div>

        {/* Phone mockup */}
        <div className="gr-sc-phone">
          <div className="gr-sc-phone-notch" />
          {c.feedImage ? (
            <div className="gr-sc-phone-feed gr-sc-phone-feed--img">
              <img src={c.feedImage} alt={c.brand} className="gr-sc-phone-feedimg" />
            </div>
          ) : (
            <div className="gr-sc-phone-feed">
              {c.phoneFeed.map((src, i) =>
                isImgUrl(src) ? (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className={`gr-sc-phone-thumb${c.phoneReels.includes(i) ? ' gr-sc-phone-reel' : ''}`}
                    style={{ objectFit: 'cover', width: '100%', aspectRatio: '1' }}
                  />
                ) : (
                  <div
                    key={i}
                    className={`gr-sc-phone-thumb${c.phoneReels.includes(i) ? ' gr-sc-phone-reel' : ''}`}
                    style={{ '--ph-grad': src } as React.CSSProperties}
                  />
                ),
              )}
            </div>
          )}
        </div>

        {/* Badges */}
        {c.pills.map((p, i) => (
          <div key={i} className={`gr-sc-badge gr-sc-badge--${p.pos}`}>
            {p.icon && <span className="gr-sc-badge-icon">{p.icon}</span>}
            <span className="gr-sc-badge-val">{p.val}</span>
            {p.label && <span className="gr-sc-badge-lbl">{p.label}</span>}
          </div>
        ))}
      </div>

      {/* ── Infos ── */}
      <div className="gr-sc-info" style={fade}>
        <div className="gr-sc-info-left">
          <div className="gr-sc-brand">{c.brand}</div>
          <div className="gr-sc-sector">
            {c.sector} · {c.platform}
          </div>
          <p className="gr-sc-story">{c.story}</p>
          <div className="gr-sc-metrics">
            {c.metrics.map((m, i) => (
              <div key={i} className="gr-sc-metric">
                <span className="gr-sc-metric-val">{m.val}</span>
                <span className="gr-sc-metric-lbl">{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="gr-sc-nav">
          <button
            className="gr-sc-nav-btn"
            onClick={() => go(Math.max(idx - 1, 0))}
            disabled={idx === 0}
            aria-label="Cas précédent"
          >
            ←
          </button>
          <div className="gr-sc-nav-dots">
            {cases.map((cas, i) => (
              <button
                key={i}
                className={`gr-sc-dot${i === idx ? ' gr-sc-dot--on' : ''}`}
                onClick={() => go(i)}
                aria-label={cas.brand}
              >
                <span className="gr-sc-dot-label">{cas.navLabel ?? cas.brand}</span>
              </button>
            ))}
          </div>
          <button
            className="gr-sc-nav-btn"
            onClick={() => go(Math.min(idx + 1, cases.length - 1))}
            disabled={idx === cases.length - 1}
            aria-label="Cas suivant"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}
